import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { PermissionGate } from '@/components/PermissionGate';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { 
  FileText, Plus, Search, Upload, DollarSign, Shield, Gavel, Eye, Trash2,
  CheckCircle, XCircle, Loader2, ArrowUpRight, Scale, GitBranch, Mail, Building2
} from 'lucide-react';
import api from '@/lib/api';
import { useProject } from '@/contexts/ProjectContext';
import ModuleInsightsPanel from '@/components/common/ModuleInsightsPanel';
import AIAnalystPanel from '@/components/ai/AIAnalystPanel';

const statusColors = {
  DRAFT: 'bg-slate-500/20 text-slate-400',
  UNDER_REVIEW: 'bg-amber-500/20 text-amber-400',
  ACTIVE: 'bg-emerald-500/20 text-emerald-400',
  SUSPENDED: 'bg-orange-500/20 text-orange-400',
  TERMINATED: 'bg-red-500/20 text-red-400',
  COMPLETED: 'bg-blue-500/20 text-blue-400',
  ARCHIVED: 'bg-gray-500/20 text-gray-400'
};

const getRiskColor = (score) => {
  if (score === null || score === undefined) return 'text-slate-400';
  if (score <= 30) return 'text-emerald-400';
  if (score <= 60) return 'text-amber-400';
  return 'text-red-400';
};

const formatINR = (value) => {
  if (!value && value !== 0) return '-';
  const absVal = Math.abs(value);
  if (absVal >= 10000000) return `${(value / 10000000).toFixed(2)} Cr`;
  if (absVal >= 100000) return `${(value / 100000).toFixed(2)} L`;
  if (absVal >= 1000) return `${(value / 1000).toFixed(1)}K`;
  return value.toLocaleString('en-IN');
};

const contractTypeLabels = {
  EPC: 'EPC', EPCM: 'EPCM', CONSTRUCTION: 'Construction', SUPPLY: 'Supply',
  SERVICES: 'Services', SUBCONTRACT: 'Subcontract', FRAMEWORK: 'Framework',
  NEC3: 'NEC3', NEC4: 'NEC4', FIDIC_RED: 'FIDIC Red', FIDIC_YELLOW: 'FIDIC Yellow',
  FIDIC_SILVER: 'FIDIC Silver', CUSTOM: 'Custom'
};

export default function ContractsModule() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { projectId: ctxProjectId } = useProject();
  // The selected project is the source of truth. A hard-coded 'proj_001'
  // fallback used to make this page read — and 'New Contract' write — Al
  // Dhafra's register no matter which project was selected.
  const projectId = searchParams.get('projectId') || ctxProjectId;
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [newContract, setNewContract] = useState({
    contract_number: '', title: '', contract_type: 'EPC',
    original_value: '', currency: 'INR', completion_date: ''
  });

  useEffect(() => {
    if (!projectId) return;
    fetchContracts();
    fetchDashboard();
  }, [projectId]);

  const fetchContracts = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/contracts/projects/${projectId}/contracts`);
      setContracts(res.data || []);
    } catch { toast.error('Failed to load contracts'); } finally { setLoading(false); }
  };

  const fetchDashboard = async () => {
    try {
      const res = await api.get(`/clm/projects/${projectId}/clm-dashboard`);
      setDashboard(res.data);
    } catch (e) { console.error('Dashboard error:', e); }
  };

  const handleCreate = async () => {
    try {
      await api.post(`/contracts/projects/${projectId}/contracts`, {
        project_id: projectId, contract_number: newContract.contract_number,
        title: newContract.title, contract_type: newContract.contract_type,
        financials: { original_value: parseFloat(newContract.original_value) || 0, currency: newContract.currency },
        dates: { completion_date: newContract.completion_date ? new Date(newContract.completion_date).toISOString() : new Date().toISOString() }
      });
      toast.success('Contract created');
      setShowNewDialog(false);
      setNewContract({ contract_number: '', title: '', contract_type: 'EPC', original_value: '', currency: 'INR', completion_date: '' });
      fetchContracts(); fetchDashboard();
    } catch { toast.error('Failed to create contract'); }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this contract and all related data?')) return;
    try {
      await api.delete(`/contracts/contracts/${id}`);
      toast.success('Deleted'); fetchContracts(); fetchDashboard();
    } catch { toast.error('Failed to delete'); }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      setImporting(true); setImportResult(null);
      const res = await api.post(`/contracts/projects/${projectId}/contracts/import`, formData, { headers: { 'Content-Type': 'multipart/form-data' }});
      setImportResult(res.data);
      if (res.data.imported_count > 0) { toast.success(`Imported ${res.data.imported_count} contracts`); fetchContracts(); fetchDashboard(); }
      if (res.data.error_count > 0) toast.warning(`${res.data.error_count} errors`);
    } catch { toast.error('Import failed'); } finally { setImporting(false); }
  };

  const filtered = contracts.filter(c => {
    const q = searchQuery.toLowerCase();
    const matchSearch = !q || c.title?.toLowerCase().includes(q) || c.contract_number?.toLowerCase().includes(q);
    const matchStatus = statusFilter === 'all' || c.status === statusFilter;
    return matchSearch && matchStatus;
  });

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="contracts-module">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Contracts & Claims Management</h1>
            <p className="text-sm text-slate-400 mt-1">Commercial governance, claims tracking, LD monitoring, and compliance</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => navigate('/contracts/portfolio')} className="border-[#3A3F4A] text-slate-300" data-testid="portfolio-view-btn">
              <Building2 className="w-4 h-4 mr-2" />Portfolio View
            </Button>
            <PermissionGate module="CLM" action="import">
              <Button variant="outline" onClick={() => setShowImportDialog(true)} className="border-[#3A3F4A] text-slate-300" data-testid="import-contracts-btn">
                <Upload className="w-4 h-4 mr-2" />Import
              </Button>
            </PermissionGate>
            <PermissionGate module="CLM" action="create">
              <Button onClick={() => setShowNewDialog(true)} className="bg-gradient-to-r from-[#1E4E8C] to-[#6B5CE7] hover:opacity-90" data-testid="new-contract-btn">
                <Plus className="w-4 h-4 mr-2" />New Contract
              </Button>
            </PermissionGate>
          </div>
        </div>

        {/* AI Analyst Panel */}
        {projectId && (
          <AIAnalystPanel projectId={projectId} moduleKey="CONTRACTS_CLAIMS" defaultOpen={false} compact={true} />
        )}

        {dashboard && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {[
              { label: 'Contracts', value: dashboard.contracts?.total || 0, sub: `${dashboard.contracts?.active || 0} active`, icon: FileText, color: '#1E4E8C' },
              { label: 'Contract Value', value: `₹${formatINR(dashboard.contracts?.total_value || 0)}`, sub: 'Total value', icon: DollarSign, color: '#10b981' },
              { label: 'Open Claims', value: dashboard.claims?.open || 0, sub: `₹${formatINR(dashboard.claims?.total_claimed || 0)}`, icon: Gavel, color: '#f59e0b' },
              { label: 'LD Exposure', value: `₹${formatINR(dashboard.ld_exposure?.total || 0)}`, sub: `${dashboard.ld_exposure?.trackers_count || 0} trackers`, icon: DollarSign, color: '#ef4444' },
              { label: 'Variations', value: dashboard.variations?.total || 0, sub: `₹${formatINR(dashboard.variations?.total_approved_value || 0)} approved`, icon: GitBranch, color: '#143A6B' },
              { label: 'Disputes', value: dashboard.disputes?.active || 0, sub: `₹${formatINR(dashboard.disputes?.total_amount || 0)}`, icon: Scale, color: '#f97316' },
              { label: 'Compliance', value: `${dashboard.compliance?.compliance_rate || 100}%`, sub: `${dashboard.compliance?.overdue || 0} overdue`, icon: Shield, color: '#10b981' },
            ].map((kpi, i) => (
              <motion.div key={kpi.label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                <Card className="bg-[#1F2937] border-[#3A3F4A]">
                  <CardContent className="p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="p-1.5 rounded" style={{ backgroundColor: `${kpi.color}20` }}>
                        <kpi.icon className="w-3.5 h-3.5" style={{ color: kpi.color }} />
                      </div>
                      <span className="text-[10px] text-slate-500 uppercase tracking-wider">{kpi.label}</span>
                    </div>
                    <p className="text-lg font-bold text-white">{kpi.value}</p>
                    <p className="text-[10px] text-slate-500">{kpi.sub}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}

        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input data-testid="contract-search" placeholder="Search contracts..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10 bg-[#1F2937] border-[#3A3F4A] text-white" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40 bg-[#1F2937] border-[#3A3F4A] text-white" data-testid="status-filter">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent className="bg-[#1F2937] border-[#3A3F4A]">
              {['all', 'DRAFT', 'UNDER_REVIEW', 'ACTIVE', 'COMPLETED', 'TERMINATED'].map(s => (
                <SelectItem key={s} value={s}>{s === 'all' ? 'All Status' : s.replace(/_/g, ' ')}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Card className="bg-[#1F2937] border-[#3A3F4A]">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 text-[#1E4E8C] animate-spin" /></div>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <FileText className="w-12 h-12 text-slate-600 mb-4" />
                <p className="text-slate-400 mb-2">No contracts found</p>
                <Button onClick={() => setShowNewDialog(true)} variant="outline" className="border-[#3A3F4A] text-slate-300">
                  <Plus className="w-4 h-4 mr-2" />New Contract
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#3A3F4A]">
                      {['Contract', 'Type', 'Value', 'Status', 'Risk', 'AI', 'Actions'].map(h => (
                        <th key={h} className={`text-${h === 'Actions' ? 'right' : 'left'} text-xs text-slate-500 font-medium px-4 py-3 uppercase tracking-wider`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((c, i) => (
                      <motion.tr key={c.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.03 }}
                        className="border-b border-[#3A3F4A] hover:bg-white/[0.02] cursor-pointer"
                        onClick={() => navigate(`/contracts/${c.id}`)} data-testid={`contract-row-${c.id}`}>
                        <td className="px-4 py-3">
                          <p className="text-sm text-white font-medium">{c.title || c.contractor || 'Untitled'}</p>
                          <p className="text-xs text-slate-500">{c.contract_number}</p>
                        </td>
                        <td className="px-4 py-3"><span className="text-sm text-slate-300">{contractTypeLabels[c.contract_type] || c.contract_type || '-'}</span></td>
                        <td className="px-4 py-3"><span className="text-sm text-white">₹{formatINR(c.financials?.original_value || c.original_value || 0)}</span></td>
                        <td className="px-4 py-3">
                          <Badge className={statusColors[c.status?.toUpperCase()] || statusColors[c.status] || 'bg-slate-500/20 text-slate-400'}>
                            {(c.status || 'DRAFT').toUpperCase()}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-lg font-bold ${getRiskColor(c.ai_analysis?.risk_score)}`}>
                            {c.ai_analysis?.risk_score ?? '-'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {c.ai_analysis?.processing_status === 'COMPLETED' ? (
                            <Badge className="bg-emerald-500/20 text-emerald-400"><CheckCircle className="w-3 h-3 mr-1" />Done</Badge>
                          ) : c.ai_analysis?.processing_status === 'PROCESSING' ? (
                            <Badge className="bg-blue-500/20 text-blue-400"><Loader2 className="w-3 h-3 mr-1 animate-spin" />Running</Badge>
                          ) : (
                            <Badge className="bg-slate-500/20 text-slate-400">Pending</Badge>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-white" onClick={() => navigate(`/contracts/${c.id}`)}>
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-red-400" onClick={() => handleDelete(c.id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* New Contract Dialog */}
        <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
          <DialogContent className="bg-[#1F2937] border-[#3A3F4A] max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white">Create New Contract</DialogTitle>
              <DialogDescription className="text-slate-400">Add a new contract. Upload documents after creation for AI analysis.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Contract Number</Label>
                  <Input data-testid="contract-number-input" placeholder="CON-2026-001" value={newContract.contract_number} onChange={e => setNewContract({ ...newContract, contract_number: e.target.value })} className="bg-[#0A0E1A] border-[#3A3F4A] text-white" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Type</Label>
                  <Select value={newContract.contract_type} onValueChange={v => setNewContract({ ...newContract, contract_type: v })}>
                    <SelectTrigger className="bg-[#0A0E1A] border-[#3A3F4A] text-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#1F2937] border-[#3A3F4A]">
                      {Object.entries(contractTypeLabels).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Title</Label>
                <Input data-testid="contract-title-input" placeholder="Main EPC Contract" value={newContract.title} onChange={e => setNewContract({ ...newContract, title: e.target.value })} className="bg-[#0A0E1A] border-[#3A3F4A] text-white" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Value</Label>
                  <Input data-testid="contract-value-input" type="number" placeholder="50000000" value={newContract.original_value} onChange={e => setNewContract({ ...newContract, original_value: e.target.value })} className="bg-[#0A0E1A] border-[#3A3F4A] text-white" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Currency</Label>
                  <Select value={newContract.currency} onValueChange={v => setNewContract({ ...newContract, currency: v })}>
                    <SelectTrigger className="bg-[#0A0E1A] border-[#3A3F4A] text-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#1F2937] border-[#3A3F4A]">
                      {['INR', 'USD', 'EUR', 'GBP', 'AED', 'SAR'].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Completion Date</Label>
                <Input data-testid="completion-date-input" type="date" value={newContract.completion_date} onChange={e => setNewContract({ ...newContract, completion_date: e.target.value })} className="bg-[#0A0E1A] border-[#3A3F4A] text-white" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowNewDialog(false)} className="text-slate-400">Cancel</Button>
              <Button onClick={handleCreate} className="bg-gradient-to-r from-[#1E4E8C] to-[#6B5CE7]" data-testid="create-contract-btn" disabled={!newContract.contract_number || !newContract.title}>Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Import Dialog */}
        <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
          <DialogContent className="bg-[#1F2937] border-[#3A3F4A] max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white">Import Contracts</DialogTitle>
              <DialogDescription className="text-slate-400">Bulk import from Excel or CSV</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="p-4 rounded-lg bg-[#0A0E1A] border border-dashed border-[#3A3F4A] text-center">
                <Upload className="w-8 h-8 text-slate-500 mx-auto mb-2" />
                <label className="cursor-pointer">
                  <input type="file" accept=".xlsx,.xls,.csv" onChange={handleImport} className="hidden" data-testid="import-file-input" />
                  <Button variant="outline" className="border-[#3A3F4A] text-slate-300" disabled={importing} asChild>
                    <span>{importing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Importing...</> : 'Select File'}</span>
                  </Button>
                </label>
              </div>
              {importResult && (
                <div className="p-3 rounded bg-[#0A0E1A] border border-[#3A3F4A] flex items-center gap-4">
                  <div className="flex items-center gap-1"><CheckCircle className="w-4 h-4 text-emerald-400" /><span className="text-sm text-emerald-400">{importResult.imported_count} imported</span></div>
                  {importResult.error_count > 0 && <div className="flex items-center gap-1"><XCircle className="w-4 h-4 text-red-400" /><span className="text-sm text-red-400">{importResult.error_count} errors</span></div>}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => { setShowImportDialog(false); setImportResult(null); }} className="text-slate-400">Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <ModuleInsightsPanel projectId={projectId} moduleKey="contracts" color="#84CC16" deepLinkBase="/contracts" />
      </div>
    </DashboardLayout>
  );
}
